import React from 'react';
import { Link } from 'react-router-dom';
import { Users, Target, UserPlus, Building2 } from 'lucide-react';
import { useGetAboutContent } from '../services';
import { Loading, Error } from '../shared/components';

const AboutPage: React.FC = () => {
  const { aboutData, isLoading, error } = useGetAboutContent();

  // Loading state
  if (isLoading) {
    return <Loading />;
  }

  // Error state
  if (error) {
    return <Error message={error} />;
  }

  return (
    <div className="py-6 sm:py-12">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg p-4 sm:p-6 lg:p-8 ">
          {/* Header */}
          <div className="mb-6 sm:mb-8">
            <div className="flex items-center mb-3 sm:mb-4">
              <h1 className="text-2xl sm:text-3xl font-bold text-primary-500">
                {aboutData?.home_title_first?.[0]?.home_title_first_title ||
                  'About Us'}
              </h1>
            </div>
            <div className="w-12 sm:w-16 h-0.5 rounded bg-primary-500"></div>
          </div>

          {/* Main Content */}
          <div className="prose prose-lg max-w-none">
            <div className="mb-6 sm:mb-8 flex items-start">
              <p className="text-base sm:text-lg leading-relaxed text-secondary-700">
                {aboutData?.home_title_first?.[0]
                  ?.home_title_second_description ||
                  'Welcome to the VLSI Club, a dynamic community of industry professionals, researchers, enthusiasts & students passionate about the world of Very-Large-Scale Integration (VLSI) and semiconductor design. Our club is dedicated to exploring the cutting-edge advancements in digital and analog circuit design, system-on-chip (SoC) development, EDA tools, fabrication technologies, and more.'}
              </p>
            </div>

            {/* Who We Are Section */}
            <div className="mb-6 sm:mb-8">
              <div className="flex items-start mb-4">
                <Building2 className="w-5 h-5 sm:w-6 sm:h-6 mr-3 mt-1 flex-shrink-0 text-primary-500" />
                <h2 className="text-lg sm:text-2xl font-semibold text-primary-500">
                  {aboutData?.home_title_second?.[0]?.home_title_second_title ||
                    'Who We Are'}
                </h2>
              </div>
              <p className="text-base sm:text-lg leading-relaxed text-secondary-700">
                {aboutData?.home_title_second?.[0]
                  ?.home_title_second_description ||
                  'Founded in collaboration with VeriFast technologies, the VLSI Club aims to bring together industry experts to discuss advancements in the Semiconductor domain. It would also serve as a forum to bridge the gap between academic concepts and real-world chip design. In short, the VLSI club will provide a collaborative platform for individuals who are eager to dive into the intricacies of microelectronics and silicon systems.'}
              </p>
            </div>

            {/* Our Mission Section */}
            <div className="mb-6 sm:mb-8">
              <div className="flex items-start mb-4">
                <Target className="w-5 h-5 sm:w-6 sm:h-6 mr-3 mt-1 flex-shrink-0 text-primary-500" />
                <h2 className="text-lg sm:text-2xl font-semibold text-primary-500">
                  {aboutData?.home_title_third?.[0]?.home_title_third_title ||
                    'Our Mission'}
                </h2>
              </div>
              <p className="text-base sm:text-lg leading-relaxed text-secondary-700">
                {aboutData?.home_title_third?.[0]
                  ?.home_title_third_description ||
                  'Our mission is to foster innovation, learning, and practical experience in the field of VLSI through workshops, hands-on projects, guest lectures, research collaborations, and participation in design contests.'}
              </p>
            </div>

            {/* Join Us Section */}
            <div className="mb-6 sm:mb-8">
              <div className="flex items-start mb-4">
                <Users className="w-5 h-5 sm:w-6 sm:h-6 mr-3 mt-1 flex-shrink-0 text-primary-500" />
                <h2 className="text-lg sm:text-2xl font-semibold text-primary-500">
                  {aboutData?.home_title_fourth?.[0]?.home_title_fourth_title ||
                    'Join Us'}
                </h2>
              </div>
              <p className="text-base sm:text-lg leading-relaxed mb-4 sm:mb-6 text-secondary-700">
                {aboutData?.home_title_fourth?.[0]
                  ?.home_title_fourth_description ||
                  "Whether you're just curious about VLSI or already deep into design flow diagrams, there's a place for you in the VLSI Club. Join us & become a member to gain hands-on experience, connect with like-minded peers, and shape the future of electronics!"}
              </p>

              {/* Call to Action Button */}
              <div className="text-center">
                <Link
                  to="/membership"
                  className="inline-flex items-center px-6 sm:px-8 py-3 sm:py-4 text-white text-sm sm:text-base font-semibold rounded-lg transition-all duration-200 cursor-pointer hover:bg-primary-600 hover:shadow-lg transform hover:scale-105 bg-primary-500"
                >
                  <UserPlus className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                  Become a Member
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
